import { format } from 'date-fns';
import { toPng } from 'html-to-image';

import { sanitizeFileName } from './filename';
import { handleDownloadMedia } from './general';
import { LogManager } from './Logger';

import type { MediaFormat } from '@src/components/common/FormatDropdown';
import type React from 'react';

function getFirst5Words(text: string): string {
  return text.trim().split(/\s+/).slice(0, 5).join(' ');
}

export interface MediaGenerationOptions {
  compositionSource: Record<string, any>;
  outputFormat: MediaFormat;
}

export const getMediaGenerationConfig = (
  format: MediaFormat,
): Partial<MediaGenerationOptions> => {
  const config: any = {};

  switch (format) {
    case 'gif':
      config.gifConfig = {
        quality: 'best',
        compression: 1,
        frameRate: '25 fps',
      };
      break;
    case 'png':
      config.snapshotConfig = {
        removeAnimations: true,
      };
      break;
    case 'mp4':
      // MP4 doesn't need additional config
      break;
  }

  return config;
};

export const generateMedia = async (
  compositionSource: Record<string, any>,
  format: MediaFormat = 'gif',
): Promise<{ url: string; datoMedia: { id: string; url: string } }> => {
  const requestBody = {
    compositionSource,
    outputFormat: format,
    ...getMediaGenerationConfig(format),
  };

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/media/generate`,
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const data = await response.json();
  return data.data;
};

export interface DownloadMediaOptions {
  imageSource?: Record<string, any>;
  imageRef?: React.RefObject<HTMLImageElement>;
  itemId?: string;
  storyTitle?: string;
  quoteText?: string;
  formatOverride?: MediaFormat;
  onDownloadStart?: () => void;
  onDownloadEnd?: () => void;
  onDownloadSuccess?: (outputFormat: MediaFormat) => void;
  onDownloadError?: (error: any) => void;
}

export const downloadMediaFromSource = async (
  e: React.MouseEvent<HTMLElement, MouseEvent>,
  options: DownloadMediaOptions,
): Promise<void> => {
  const {
    imageSource,
    imageRef,
    itemId,
    storyTitle,
    quoteText,
    formatOverride,
    onDownloadStart,
    onDownloadEnd,
    onDownloadSuccess,
    onDownloadError,
  } = options;

  e.preventDefault();
  e.stopPropagation();

  try {
    let outputFormat: MediaFormat = 'png';
    if (imageSource || (imageRef && imageRef.current)) {
      onDownloadStart?.();

      let url = '';
      if (imageSource) {
        outputFormat = formatOverride || outputFormat;
        const result = await generateMedia(imageSource, outputFormat);
        url = result?.url;
      } else if (imageRef && imageRef.current) {
        url = await toPng(imageRef.current);
      }

      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const namePart = storyTitle || 'Quote';
      const quotePart = quoteText ? getFirst5Words(quoteText) : itemId || '';
      const media = {
        url: url,
        fileName: sanitizeFileName(`${namePart} - ${quotePart} - ${dateStr}`),
        id: itemId,
      };

      await handleDownloadMedia(e, media);
      onDownloadSuccess?.(outputFormat);
    }
  } catch (error) {
    LogManager.getLogger('utility:downloadMediaFromSource').error(
      'Download failed:',
      error,
    );
    onDownloadError?.(error);
  } finally {
    onDownloadEnd?.();
  }
};
